import { useEffect, useMemo, useState } from 'react'
import { api, ApiError, type AdminUser } from '../api'
import { useAuth } from '../auth'
import { Topbar } from '../components/Topbar'
import { StatusLight } from '../components/StatusLight'
import { Button } from '../components/Button'
import { Input } from '../components/Input'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { EmptyState } from '../components/EmptyState'
import { useToast } from '../components/Toast'

type Mode =
  | { kind: 'edit'; userId: string }
  | { kind: 'create' }

function relativeTime(iso: string | null): string {
  if (!iso) return 'never'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return 'never'
  const delta = Date.now() - d.getTime()
  const min = Math.floor(delta / 60000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.floor(hr / 24)
  if (day < 30) return `${day}d ago`
  return d.toISOString().slice(0, 10)
}

export function AdminUsersPage() {
  const { user: me } = useAuth()
  const toast = useToast()
  const [users, setUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [mode, setMode] = useState<Mode>({ kind: 'edit', userId: '' })
  const selected = useMemo(
    () => (mode.kind === 'edit' ? users.find((u) => u.id === mode.userId) ?? null : null),
    [mode, users],
  )

  async function refresh() {
    setLoading(true)
    setLoadError(null)
    try {
      const list = await api.listUsers()
      setUsers(list)
    } catch (e) {
      setLoadError(
        e instanceof ApiError
          ? `Couldn't load users (HTTP ${e.status}). Check the server.`
          : 'Network error — retry or refresh.',
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
  }, [])

  // Auto-select first user after load
  useEffect(() => {
    if (mode.kind === 'edit' && !mode.userId && users.length > 0) {
      setMode({ kind: 'edit', userId: users[0].id })
    }
  }, [users, mode])

  if (me && !me.isOwner) {
    return (
      <div className="page">
        <Topbar />
        <main className="page-main">
          <EmptyState
            mark="restricted"
            title="Owner only"
            body="You need an owner account to manage users."
          />
        </main>
      </div>
    )
  }

  return (
    <div className="page">
      <Topbar />
      <main className="page-main">
        <div className="page-heading">
          <div>
            <h1>Users</h1>
            <div className="page-heading-sub">Manage accounts, status, and feature assignments.</div>
          </div>
          <Button
            variant="primary"
            onClick={() => setMode({ kind: 'create' })}
          >
            New user
          </Button>
        </div>

        <div className="admin-grid">
          <UsersList
            users={users}
            loading={loading}
            loadError={loadError}
            selectedId={mode.kind === 'edit' ? mode.userId : null}
            onSelect={(id) => setMode({ kind: 'edit', userId: id })}
            onRetry={refresh}
          />
          {mode.kind === 'create' ? (
            <CreateUserPanel
              onCancel={() => {
                if (users[0]) setMode({ kind: 'edit', userId: users[0].id })
              }}
              onCreated={(u) => {
                setUsers((prev) => [...prev, u])
                setMode({ kind: 'edit', userId: u.id })
                toast.show('User created', 'info')
              }}
            />
          ) : selected ? (
            <EditUserPanel
              key={selected.id}
              user={selected}
              onUpdated={(u) => {
                setUsers((prev) => prev.map((x) => (x.id === u.id ? u : x)))
                toast.show('Saved', 'info')
              }}
              onDeleted={(id) => {
                setUsers((prev) => prev.filter((x) => x.id !== id))
                const next = users.find((x) => x.id !== id)
                if (next) setMode({ kind: 'edit', userId: next.id })
                toast.show('User deleted', 'info')
              }}
              onDisabledToggle={(u) => {
                setUsers((prev) => prev.map((x) => (x.id === u.id ? u : x)))
                toast.show(u.isActive ? 'Disabled' : 'Enabled', 'info')
              }}
              onError={(msg) => toast.show(msg, 'error')}
            />
          ) : (
            <div className="admin-detail">
              <div className="muted">Select a user on the left, or create a new one.</div>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

function UsersList({
  users,
  loading,
  loadError,
  selectedId,
  onSelect,
  onRetry,
}: {
  users: AdminUser[]
  loading: boolean
  loadError: string | null
  selectedId: string | null
  onSelect: (id: string) => void
  onRetry: () => void
}) {
  return (
    <div className="admin-list">
      <div className="admin-list-header">
        <span>accounts</span>
        <span>{users.length}</span>
      </div>
      <div className="admin-list-items">
        {loading && <div className="admin-list-item muted">Loading…</div>}
        {loadError && (
          <div className="admin-list-item">
              <div className="admin-list-item-text">
              <div className="admin-list-item-primary inline-error-text">
                {loadError}
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={onRetry}
              >
                Retry
              </button>
            </div>
          </div>
        )}
        {!loading && !loadError && users.length === 0 && (
          <div className="admin-list-item muted">No users yet.</div>
        )}
        {users.map((u) => (
          <button
            key={u.id}
            type="button"
            className={`admin-list-item ${selectedId === u.id ? 'active' : ''}`}
            onClick={() => onSelect(u.id)}
          >
            <span aria-hidden="true">
              <StatusLight state={u.isActive ? 'on' : 'off'} />
            </span>
            <div className="admin-list-item-text">
              <div className="admin-list-item-primary">
                {u.displayName ?? u.username}
                {u.isOwner && (
                  <span className="admin-pill list-item-meta-pill">
                    owner
                  </span>
                )}
              </div>
              <div className="admin-list-item-secondary">{u.username}</div>
            </div>
            <div className="admin-list-item-meta">{relativeTime(u.lastLoginAt)}</div>
          </button>
        ))}
      </div>
    </div>
  )
}

function CreateUserPanel({
  onCancel,
  onCreated,
}: {
  onCancel: () => void
  onCreated: (u: AdminUser) => void
}) {
  const toast = useToast()
  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (username.trim().length < 1) {
      setError('Username is required.')
      return
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    setBusy(true)
    try {
      const { id } = await api.createUser({
        username: username.trim(),
        password,
        displayName: displayName.trim() || undefined,
      })
      // Fetch the user to populate the detail panel
      const list = await api.listUsers()
      const created = list.find((u) => u.id === id)
      if (created) {
        onCreated(created)
      } else {
        toast.show('User created', 'info')
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setError('That username is already taken.')
      } else if (e instanceof ApiError) {
        setError(`Couldn't create user (HTTP ${e.status}).`)
      } else {
        setError("Couldn't reach the server. Check your connection.")
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="admin-detail" onSubmit={onSubmit}>
      <div className="admin-detail-header">
        <div>
          <div className="admin-detail-title">New user</div>
          <div className="admin-detail-sub">create account</div>
        </div>
      </div>

      <div className="admin-detail-section">
        <div className="admin-detail-section-label">Identity</div>
        <Input
          label="Username"
          name="username"
          autoComplete="off"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          hint="Used to sign in. Lowercase, no spaces."
          disabled={busy}
          required
        />
        <Input
          label="Display name"
          name="displayName"
          autoComplete="off"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          hint="Optional. Shown in the topbar instead of the username."
          disabled={busy}
        />
      </div>

      <hr className="admin-divider" />

      <div className="admin-detail-section">
        <div className="admin-detail-section-label">Sign-in</div>
        <Input
          label="Initial password"
          name="password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint="At least 8 characters. The user can change it after signing in."
          error={error}
          disabled={busy}
          required
        />
      </div>

      <div className="admin-actions-end">
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={busy}>
          {busy ? 'Creating…' : 'Create user'}
        </Button>
      </div>
    </form>
  )
}

function EditUserPanel({
  user,
  onUpdated,
  onDeleted,
  onDisabledToggle,
  onError,
}: {
  user: AdminUser
  onUpdated: (u: AdminUser) => void
  onDeleted: (id: string) => void
  onDisabledToggle: (u: AdminUser) => void
  onError: (msg: string) => void
}) {
  const [displayName, setDisplayName] = useState(user.displayName ?? '')
  const [isActive, setIsActive] = useState(user.isActive)
  const [busy, setBusy] = useState(false)
  const [resetOpen, setResetOpen] = useState(false)
  const [resetPw, setResetPw] = useState('')
  const [resetBusy, setResetBusy] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteBusy, setDeleteBusy] = useState(false)

  // re-sync when user prop changes
  useEffect(() => {
    setDisplayName(user.displayName ?? '')
    setIsActive(user.isActive)
  }, [user.id, user.displayName, user.isActive])

  async function onSave(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      await api.updateUser(user.id, {
        displayName: displayName.trim() || null,
        isActive,
      })
      onUpdated({
        ...user,
        displayName: displayName.trim() || null,
        isActive,
      })
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        onError("Couldn't save — owner accounts can't be disabled.")
      } else {
        onError(`Couldn't save (HTTP ${err instanceof ApiError ? err.status : '?'}).`)
      }
    } finally {
      setBusy(false)
    }
  }

  async function onConfirmReset() {
    if (resetPw.length < 8) {
      onError('New password must be at least 8 characters.')
      return
    }
    setResetBusy(true)
    try {
      await api.resetPassword(user.id, resetPw)
      setResetOpen(false)
      setResetPw('')
      onUpdated({ ...user })
    } catch (err) {
      onError(`Couldn't reset password (HTTP ${err instanceof ApiError ? err.status : '?'}).`)
    } finally {
      setResetBusy(false)
    }
  }

  async function onConfirmDelete() {
    setDeleteBusy(true)
    try {
      await api.deleteUser(user.id)
      onDeleted(user.id)
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        onError("Owner accounts can't be deleted.")
      } else {
        onError(`Couldn't delete (HTTP ${err instanceof ApiError ? err.status : '?'}).`)
      }
    } finally {
      setDeleteBusy(false)
    }
  }

  return (
    <>
      <form className="admin-detail" onSubmit={onSave}>
        <div className="admin-detail-header">
          <div>
            <div className="admin-detail-title">{user.displayName ?? user.username}</div>
            <div className="admin-detail-sub">{user.username}</div>
          </div>
          <span className="admin-pill">
            <StatusLight state={isActive ? 'on' : 'off'} />
            {isActive ? 'active' : 'disabled'}
          </span>
        </div>

        <div className="admin-detail-section">
          <div className="admin-detail-section-label">Identity</div>
          <Input
            label="Display name"
            name="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            hint="Shown in the topbar. Leave blank to use the username."
            disabled={busy}
          />
          <div className="admin-form-row">
            <div className="field">
              <span className="field-label">Created</span>
              <div className="mono-caption">{new Date(user.createdAt).toISOString().slice(0, 10)}</div>
            </div>
            <div className="field">
              <span className="field-label">Last sign-in</span>
              <div className="mono-caption">{user.lastLoginAt ? new Date(user.lastLoginAt).toISOString().slice(0, 16).replace('T', ' ') : 'never'}</div>
            </div>
          </div>
        </div>

        <hr className="admin-divider" />

        <div className="admin-detail-section">
          <div className="admin-detail-section-label">Access</div>
          <label className="admin-toggle">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              disabled={busy || user.isOwner}
            />
            <span>{user.isOwner ? 'Owner account — always active' : 'Active — can sign in'}</span>
          </label>
        </div>

        <div className="admin-actions-end">
          <Button
            variant="secondary"
            type="button"
            onClick={() => setResetOpen(true)}
            disabled={busy}
          >
            Reset password
          </Button>
          <Button variant="primary" type="submit" disabled={busy || user.isOwner}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </div>

        {!user.isOwner && (
          <>
            <hr className="admin-divider" />
            <div className="admin-detail-section">
              <div className="admin-detail-section-label">Danger zone</div>
              <div className="confirm-block danger">
                <div className="confirm-block-title">Delete this user</div>
                <div className="confirm-block-body">
                  Removes the account and every feature assignment. The audit log keeps a record of who it was.
                </div>
                <div>
                  <Button
                    variant="danger"
                    onClick={() => setDeleteOpen(true)}
                    disabled={busy}
                  >
                    Delete user
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </form>

      <ConfirmDialog
        open={resetOpen}
        title="Reset password"
        body={
          <div className="stack">
            <div>
              Set a new password for <strong>{user.username}</strong>. They'll need it to sign in.
            </div>
            <Input
              label="New password"
              type="password"
              value={resetPw}
              onChange={(e) => setResetPw(e.target.value)}
              hint="At least 8 characters."
              autoComplete="new-password"
              autoFocus
            />
          </div>
        }
        confirmLabel="Reset password"
        busy={resetBusy}
        onConfirm={onConfirmReset}
        onCancel={() => {
          setResetOpen(false)
          setResetPw('')
        }}
      />

      <ConfirmDialog
        open={deleteOpen}
        title="Delete user"
        body={
          <div>
            Delete <strong>{user.username}</strong>? This can't be undone — the account and its feature assignments are removed.
          </div>
        }
        confirmLabel="Delete user"
        danger
        busy={deleteBusy}
        onConfirm={onConfirmDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </>
  )
}
